import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { datetimeLocalToIso } from './datetime-local';
import { type CouponFormValues } from './schemas';
import type { Coupon, CouponInput, CouponUsage } from './types';

import { apiRequest, type ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/**
 * `[PROVISOIRE — OQ-25]` — every hook here calls exactly one real endpoint
 * from `coupon.controller.ts`. `@RequireRole('merchant_owner')` on all four
 * server-side; this file adds no client-side authority check of its own —
 * `PromotionListPage` gates the whole screen on `useActiveShop()`'s
 * `activeMembership?.roleKey`, the same posture `StaffPage` already takes.
 */

interface CouponUsagesResponse {
  readonly usages: readonly CouponUsage[];
}

function couponToBody(values: CouponFormValues): CouponInput {
  return {
    code: values.code,
    type: values.type,
    value: values.value,
    minOrderAmount: values.minOrderAmount,
    usageLimitTotal: values.usageLimitTotal,
    usageLimitPerCustomer: values.usageLimitPerCustomer,
    validFrom: datetimeLocalToIso(values.validFrom),
    validUntil: datetimeLocalToIso(values.validUntil),
    isActive: values.isActive,
  };
}

export function useCouponsQuery(): UseQueryResult<readonly Coupon[], ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: ['coupons', activeShopId],
    queryFn: () =>
      apiRequest<readonly Coupon[]>('/merchant/coupons', {
        accessToken,
        shopId: activeShopId,
      }),
    enabled: accessToken !== null && activeShopId !== null,
  });
}

export function useCreateCouponMutation(): UseMutationResult<Coupon, ApiError, CouponFormValues> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: CouponFormValues) =>
      apiRequest<Coupon>('/merchant/coupons', {
        method: 'POST',
        body: couponToBody(values),
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coupons', activeShopId] });
    },
  });
}

/**
 * PUT is the ONLY write route on an existing coupon — there is no separate
 * "toggle status" endpoint (unlike `staff`'s dedicated `PATCH .../status`).
 * `CouponTable`'s activate/deactivate action calls this with the coupon's
 * own current field set and only `isActive` flipped, the full-replace
 * semantics `coupon-repository.ts`'s own header documents.
 *
 * The id is part of the mutate-time argument, not a hook parameter —
 * `PromotionListPage` is a single list+modal screen where the edited
 * coupon varies per click, unlike `ProductDetailPage`'s own
 * `useUpdateProductMutation(productId)`, which has one fixed id for the
 * whole page. Mirrors `useSetStaffStatusMutation`'s own shape instead.
 */
export function useUpdateCouponMutation(): UseMutationResult<
  Coupon,
  ApiError,
  { couponId: string; values: CouponFormValues }
> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ couponId, values }) =>
      apiRequest<Coupon>(`/merchant/coupons/${couponId}`, {
        method: 'PUT',
        body: couponToBody(values),
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coupons', activeShopId] });
    },
  });
}

/** `{ usages: [...] }` — an enveloped object, unlike `GET /merchant/coupons`'s bare array. */
export function useCouponUsagesQuery(
  couponId: string | undefined,
): UseQueryResult<readonly CouponUsage[], ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: ['coupons', activeShopId, couponId, 'usages'],
    queryFn: async () => {
      const response = await apiRequest<CouponUsagesResponse>(
        `/merchant/coupons/${couponId ?? ''}/usages`,
        { accessToken, shopId: activeShopId },
      );
      return response.usages;
    },
    enabled: accessToken !== null && activeShopId !== null && couponId !== undefined,
  });
}
